import { useCallback, useEffect, useRef } from "react";
import { VideoEngine, pruneVideoCache } from "@/lib/videoEngine";
import { useScreenCommands } from "@/hooks/useScreenCommands";
import { useConnectivity } from "@/hooks/useConnectivity";
import { useScheduledRefresh } from "@/hooks/useScheduledRefresh";
import { isApiReachable } from "@/lib/api";
import { useConfigWatchdog } from "@/hooks/useConfigWatchdog";
import type { PlayerConfig } from "@/hooks/useScreenConfig";

/** File extensions the video engine will attempt. Anything else is skipped. */
const VIDEO_EXT = /\.(mp4|webm|m4v|mov|ogv)(\?.*)?$/i;

type ContentItem =
  | { type: "video"; url: string; duration: number }
  | { type: "slideshow"; duration: number };

interface StageProps {
  screenId: string;
  config: PlayerConfig;
  onRefetchConfig: () => Promise<boolean>;
}

export function Stage({ screenId, config, onRefetchConfig }: StageProps) {
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const slideshowContainerRef = useRef<HTMLDivElement>(null);
  const loaderRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const engineRef = useRef<VideoEngine | null>(null);

  const rotationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const slideshowTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const rotationIndexRef = useRef(0);
  const configRef = useRef(config);
  configRef.current = config;

  // ── Screen id badge (5s flash on boot) ──────────────────────────────────
  const badgeRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = badgeRef.current;
    if (!el) return;
    el.classList.add("show");
    const t = setTimeout(() => el.classList.remove("show"), 5000);
    return () => clearTimeout(t);
  }, []);

  // ── Fullscreen ───────────────────────────────────────────────────────────
  const enterFullscreen = useCallback(() => {
    const elem = document.documentElement as HTMLElement & {
      webkitRequestFullscreen?: () => void;
    };
    try {
      if (elem.requestFullscreen) {
        elem.requestFullscreen().catch((err) => console.log("Fullscreen blocked:", err));
      } else if (elem.webkitRequestFullscreen) {
        elem.webkitRequestFullscreen();
      }
    } catch (e) {
      console.log("Fullscreen API error:", e);
    }
  }, []);

  useEffect(() => {
    enterFullscreen();
  }, [enterFullscreen]);

  // ── Cache & reload utilities ─────────────────────────────────────────────
  const statusRef = useRef<HTMLDivElement>(null);
  const showStatus = useCallback((msg: string) => {
    const el = statusRef.current;
    if (!el) return;
    el.textContent = msg;
    el.classList.add("show");
  }, []);
  const hideStatus = useCallback(() => statusRef.current?.classList.remove("show"), []);

  const forceReload = useCallback(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("cb", Date.now().toString());
    window.location.href = url.toString();
  }, []);

  const clearCacheAndReload = useCallback(() => {
    showStatus("Clearing TV cache...");
    // Wipe cached content, but carry the bookkeeping keys across the clear.
    // `lastCommandAt` is the record of "this command is already applied"; if
    // it goes, catchUp() re-fetches this very clear_cache on the way back up
    // and applies it again — an unbreakable reload loop. `lastRefreshOn`
    // would likewise re-arm the nightly refresh within the same hour.
    //
    // Only ever touch the player's own "signage." keys and "signage-" caches.
    // The player shares an origin with the admin dashboard (/signage-admin)
    // and the main site, and storage is per-origin: a blanket
    // localStorage.clear() wiped the dashboard's Supabase login session, so
    // the next command an admin sent failed with "Unauthorized".
    const PRESERVE = ["signage.lastCommandAt.", "signage.lastRefreshOn."];
    for (const k of Object.keys(localStorage)) {
      if (k.startsWith("signage.") && !PRESERVE.some((p) => k.startsWith(p))) {
        localStorage.removeItem(k);
      }
    }
    for (const k of Object.keys(sessionStorage)) {
      if (k.startsWith("signage.")) sessionStorage.removeItem(k);
    }
    if ("caches" in window) {
      caches
        .keys()
        .then((keys) => Promise.all(keys.filter((k) => k.startsWith("signage-")).map((k) => caches.delete(k))))
        .then(() => setTimeout(forceReload, 500));
    } else {
      setTimeout(forceReload, 1000);
    }
  }, [forceReload, showStatus]);

  // ── Video engine (persists across the whole lifetime, like the legacy
  //    single <video> element — reused across rotations to preserve buffer) ──
  const getVideo = useCallback(() => videoRef.current!, []);
  const replaceVideo = useCallback((next: HTMLVideoElement) => {
    videoRef.current = next;
  }, []);

  const fallbackToImages = useCallback(() => {
    const cfg = configRef.current;
    if (cfg.images.length > 0) {
      console.log("Falling back to image slideshow");
      videoContainerRef.current?.classList.remove("active");
      loadSlideshow(cfg);
      slideshowContainerRef.current?.classList.add("active");
    } else {
      showStatus("Video unavailable - retrying in 30s");
      setTimeout(() => {
        hideStatus();
        const engine = engineRef.current;
        if (engine) engine.loadVideo(engine.activeUrl);
      }, 30000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!videoContainerRef.current || !loaderRef.current || !videoRef.current) return;
    engineRef.current = new VideoEngine({
      container: videoContainerRef.current,
      loader: loaderRef.current,
      getVideo,
      replaceVideo,
      onExhausted: fallbackToImages,
    });
    return () => engineRef.current?.destroy();
  }, [getVideo, replaceVideo, fallbackToImages]);

  // ── Slideshow ─────────────────────────────────────────────────────────────
  function loadSlideshow(cfg: PlayerConfig) {
    const container = slideshowContainerRef.current;
    if (!container) return;
    container.innerHTML = "";

    cfg.images.forEach((url, i) => {
      if (!url.trim()) return;
      const slide = document.createElement("div");
      slide.className = "slide" + (i === 0 ? " active" : "");
      const img = document.createElement("img");
      img.src = url;
      slide.appendChild(img);
      container.appendChild(slide);
    });

    if (slideshowTimerRef.current) clearInterval(slideshowTimerRef.current);

    if (cfg.images.length > 1) {
      const slides = container.querySelectorAll(".slide");
      let current = 0;
      slideshowTimerRef.current = setInterval(() => {
        slides[current].classList.remove("active");
        current = (current + 1) % slides.length;
        slides[current].classList.add("active");
      }, (cfg.slideshow_interval || 5) * 1000);
    }
  }

  const showContent = useCallback((item: ContentItem, cfg: PlayerConfig) => {
    videoContainerRef.current?.classList.remove("active");
    slideshowContainerRef.current?.classList.remove("active");
    if (slideshowTimerRef.current) {
      clearInterval(slideshowTimerRef.current);
      slideshowTimerRef.current = null;
    }

    if (item.type === "video") {
      engineRef.current?.loadVideo(item.url);
    } else {
      loadSlideshow(cfg);
      slideshowContainerRef.current?.classList.add("active");
    }
  }, []);

  const rotateContent = useCallback(
    (content: ContentItem[], cfg: PlayerConfig) => {
      const item = content[rotationIndexRef.current];
      if (!item) {
        rotationIndexRef.current = 0;
        rotateContent(content, cfg);
        return;
      }
      showContent(item, cfg);
      if (rotationTimerRef.current) clearTimeout(rotationTimerRef.current);
      rotationTimerRef.current = setTimeout(() => {
        rotationIndexRef.current = (rotationIndexRef.current + 1) % content.length;
        rotateContent(content, cfg);
      }, item.duration);
    },
    [showContent],
  );

  const initContent = useCallback(
    (cfg: PlayerConfig) => {
      if (rotationTimerRef.current) clearTimeout(rotationTimerRef.current);
      if (slideshowTimerRef.current) clearInterval(slideshowTimerRef.current);

      const enabled: ContentItem[] = [];
      // Only real video files go to the video engine. An image pasted into the
      // Video URLs box can never decode, so it would fail, retry and
      // re-download forever — screen 7 had a PNG there doing exactly that.
      const videoUrls = cfg.videos.map((u) => u.trim()).filter(Boolean);
      const playable = videoUrls.filter((u) => VIDEO_EXT.test(u));
      for (const skipped of videoUrls.filter((u) => !VIDEO_EXT.test(u))) {
        console.warn("Skipping non-video file in Video URLs (move it to Image URLs):", skipped);
      }
      pruneVideoCache(playable);
      playable.forEach((url) => {
        enabled.push({ type: "video", url, duration: (cfg.video_duration || 30) * 1000 });
      });
      if (cfg.images.length > 0) {
        enabled.push({ type: "slideshow", duration: (cfg.slideshow_duration || 20) * 1000 });
      }

      if (enabled.length === 0) {
        showStatus("No content configured");
        return;
      }
      hideStatus();

      if (cfg.autorotate && enabled.length > 1) {
        rotationIndexRef.current = 0;
        rotateContent(enabled, cfg);
      } else {
        showContent(enabled[0], cfg);
      }
    },
    [rotateContent, showContent, showStatus, hideStatus],
  );

  // ── Apply config: orientation + ticker + content, on every config change ──
  useEffect(() => {
    document.body.className = config.orientation ?? "landscape";

    const ticker = document.getElementById("ticker");
    const tickerText = document.getElementById("ticker-text");
    if (ticker && tickerText) {
      if (config.ticker_enabled && config.ticker_text) {
        ticker.classList.add("active");
        tickerText.textContent = config.ticker_text;
        (tickerText as HTMLElement).style.animation = `scroll-left ${config.ticker_speed}s linear infinite`;
      } else {
        ticker.classList.remove("active");
      }
    }

    if (!engineRef.current) return; // wait for engine to mount first
    initContent(config);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, initContent]);

  // ── Orientation on resize (portrait TVs rotated via CSS transform) ────────
  useEffect(() => {
    const handler = () => {
      document.body.className = configRef.current.orientation ?? "landscape";
    };
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  // ── Live TV commands ───────────────────────────────────────────────────────
  useScreenCommands(screenId, {
    onReload: forceReload,
    onClearCache: clearCacheAndReload,
    onFullscreen: enterFullscreen,
  });

  // ── Reconnect recovery ─────────────────────────────────────────────────────
  const handleReconnect = useCallback(async () => {
    await onRefetchConfig();
    engineRef.current?.recoverFromOffline();
  }, [onRefetchConfig]);
  useConnectivity(handleReconnect);

  // ── Unattended-uptime maintenance ──────────────────────────────────────────
  // The TVs are never powered off, so nothing else would ever give the
  // browser a fresh start or notice a Realtime socket that died quietly.
  useScheduledRefresh(screenId, forceReload);
  useConfigWatchdog(onRefetchConfig);

  // ── Resume playback when tab/window regains visibility ─────────────────────
  useEffect(() => {
    const handler = () => {
      if (!document.hidden && engineRef.current?.activeUrl) {
        const video = videoRef.current;
        if (video && video.paused) video.play().catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", handler);
    return () => document.removeEventListener("visibilitychange", handler);
  }, []);

  // ── Daily reload (wait for REAL connectivity before reloading) ──────────────
  // navigator.onLine only reflects the network interface, not whether the
  // internet path actually works — it can read "online" during a genuine
  // outage. Navigating away on that alone risks landing on the browser's own
  // error page, where nothing can retry or report it: a dead screen with no
  // trace, found only by someone noticing. A real request is checked first.
  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>;
    let cancelled = false;
    async function scheduledDailyReload() {
      if (navigator.onLine && (await isApiReachable())) {
        if (!cancelled) forceReload();
        return;
      }
      if (!cancelled) timeout = setTimeout(scheduledDailyReload, 60 * 1000);
    }
    timeout = setTimeout(scheduledDailyReload, 24 * 60 * 60 * 1000);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [forceReload]);

  return (
    <>
      <div id="main-container">
        <div id="content-area">
          <div id="video-container" className="content-container" ref={videoContainerRef}>
            <video
              id="video-player"
              autoPlay
              muted
              playsInline
              preload="auto"
              ref={(el) => {
                if (el) videoRef.current = el;
              }}
            />
            <div className="loading" ref={loaderRef}>
              <div className="spinner" />
            </div>
          </div>
          <div id="slideshow-container" className="content-container" ref={slideshowContainerRef} />
        </div>
        <div id="ticker">
          <div id="ticker-text" />
        </div>
      </div>

      <div id="screen-id" ref={badgeRef}>
        Screen {screenId}
      </div>
      <div id="status" ref={statusRef}>
        Loading...
      </div>
    </>
  );
}
